import { useCallback, useEffect, useMemo } from 'react'
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro'
import { isNil } from 'lodash'
import { useBooleanFlagValue } from '@openfeature/react-sdk'

import { SidebarConfigProps } from '../../reducers/sidebar.slice'
import { MASS_MESSAGE_ADMIN, QA_PAGE_ENABLED } from '../../utils/adminHelpers'
import {
  getCurrentUser,
  getUserIsAdmin,
  isCanceled,
  isFreeTrialPromoCode,
  isEmailVerified,
} from '../../selectors/user.selectors'
import { selectYourTurnNumber } from '../../features/Conversations/conversation.slice'
import { fetchUnreadMessageCount } from '../../features/Conversations/actions'
import {
  fetchPayrollCompany,
  fetchPayrollProfileIfNeeded,
} from '../../features/Payroll/payrollActions'
import { filterNulls, useAppDispatch } from '../../utils/typeHelpers'
import { useLoadedFlagValue, useReselector } from '../../utils/sharedHooks'
import {
  fetchSubscriptions,
  selectIsBasicPlan,
  selectIsBasicPlanWithoutQTE,
} from '../../reducers/subscription.slice'
import {
  FEATURE_FLAG_KEYS,
  useSetOpenFeatureTargetingKey,
} from '../../features/OpenFeature'
import {
  FETCH_ALL_ANNUAL_TAX_DETAILS_KEY,
  fetchAllAnnualTaxDetailsIfNeeded,
} from '../../features/Admin/AnnualTaxDetails/annualTaxDetails.slice'
import {
  FETCH_ANNUAL_TAX_FILING_FORMS_KEY,
  fetchAnnualTaxFilingFormsIfNeeded,
} from '../../features/Taxes/AnnualTaxes/annualTaxFilingForms.slice'
import {
  FETCH_TAX_FILINGS_KEY,
  fetchAnnualTaxFilingsIfNeeded,
} from '../../features/Taxes/AnnualTaxes/annualTaxFilings.slice'
import { getIsFetching, getIsFetchingOrNotStarted } from '../../reducers/fetch'
import { selectFinancialAdvisoryProfile } from '../../features/FinancialAdvisory/financialAdvisory.selectors'
import { FinancialAdvisoryProfilesStatus } from '../../features/FinancialAdvisory/financialAdvisory.slice'
import { userHasFinancialAdvisoryAccess } from '../../features/FinancialAdvisory/service'
import { defaultMessagesURL } from '../../features/Zendesk/helpers'
import { fetchUserYearEndModuleStatuses } from '../../features/YearEndModuleStatus/yearEndModuleStatus.slice'
import { selectPayrollNavConfig } from './navigation.selectors'
import NavigationCountIcon from './NavigationCountIcon'
import { useRouteMapper } from '../Routes/useRouteMapper'
import {
  hasPermissionsForRoute,
  selectUserPermissions,
} from '@/reducers/auth/permission.selectors'
import { FETCH_USER_PERMISSIONS_KEY } from '@/actions/permissionActions'

export const MESSAGES_ROUTE_KEY = 'messages'

export type SidebarConfig = Record<string, SidebarConfigProps>

export const useSidebarConfig = (): SidebarConfig | null => {
  const dispatch = useAppDispatch()
  const isAdmin = useReselector(getUserIsAdmin)
  const user = useReselector(getCurrentUser)
  const userEmailVerified = useReselector(isEmailVerified)
  const messageCount = useReselector(selectYourTurnNumber)
  // TODO: Update as part of https://linear.app/heard/issue/ZEN-2064/remove-backwards-compatibility-for-priceproduct-id-checks
  const isPermissionCheckEnabled = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.enableStripeEntitlementPermissionChecks,
    false
  )
  const payrollNavConfig = useReselector(
    selectPayrollNavConfig,
    isPermissionCheckEnabled
  )
  const { ALL_ROUTES } = useRouteMapper()
  const userPermissions = useReselector(selectUserPermissions)
  const isFetchingPermissions = useReselector(
    getIsFetching,
    FETCH_USER_PERMISSIONS_KEY
  )
  const fetchingAnnualTaxDetails = useReselector(
    getIsFetchingOrNotStarted,
    FETCH_ALL_ANNUAL_TAX_DETAILS_KEY
  )

  const fetchingAnnualTaxFilings = useReselector(
    getIsFetchingOrNotStarted,
    FETCH_TAX_FILINGS_KEY
  )

  const fetchingAnnualTaxFilingForms = useReselector(
    getIsFetchingOrNotStarted,
    FETCH_ANNUAL_TAX_FILING_FORMS_KEY
  )

  const annualTaxFormsLoading =
    fetchingAnnualTaxDetails ||
    fetchingAnnualTaxFilings ||
    fetchingAnnualTaxFilingForms

  const shouldDisplayPracticeInsights = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.consolidatedReportsFinancialOverview,
    false
  )

  const shouldDisplayIncomePlanning = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.enableIncomePlanning,
    false
  )

  const shouldEnableEarlyMessagesAccess = useLoadedFlagValue(
    FEATURE_FLAG_KEYS.enableEarlyMessagesAccess,
    false
  )

  const showReferralsLink = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.referralExperienceEnabled,
    false
  )

  useSetOpenFeatureTargetingKey()

  const getConfigWithPermissions = useCallback(
    (config: SidebarConfig) => {
      if (
        !isFetchingPermissions &&
        userPermissions &&
        isPermissionCheckEnabled
      ) {
        Object.entries(config).forEach(([key, c]) => {
          if (c.to) {
            const hasPermission = hasPermissionsForRoute(
              userPermissions,
              ALL_ROUTES,
              c.to
            )
            config[key] = {
              ...c,
              icon: hasPermission ? c.icon : regular('lock'),
            }
          }
          if (c.pages) {
            c.pages.forEach((page) => {
              if (page.to) {
                const hasPermission = hasPermissionsForRoute(
                  userPermissions,
                  ALL_ROUTES,
                  page.to
                )
                page.icon = hasPermission ? page.icon : regular('lock')
              }
            })
          }
        })
      }
      return config
    },
    [
      isFetchingPermissions,
      userPermissions,
      ALL_ROUTES,
      isPermissionCheckEnabled,
    ]
  )

  useEffect(() => {
    if (user?.id && !isAdmin) {
      dispatch(fetchAllAnnualTaxDetailsIfNeeded())
      dispatch(fetchAnnualTaxFilingsIfNeeded())
      dispatch(fetchAnnualTaxFilingFormsIfNeeded())
      dispatch(fetchPayrollCompany())
      dispatch(fetchPayrollProfileIfNeeded())
      dispatch(fetchSubscriptions())
      dispatch(fetchUnreadMessageCount())
      dispatch(fetchUserYearEndModuleStatuses())
    }
  }, [user?.id, dispatch, isAdmin])

  const isBasicPlan = useReselector(selectIsBasicPlan)
  const isBasicPlanWithoutQTE = useReselector(selectIsBasicPlanWithoutQTE)
  const isCanceledAccount = useReselector(isCanceled)
  const hasFreeTrialPromoCode = useReselector(isFreeTrialPromoCode)

  const financialAdvisoryPilotEnabled = userHasFinancialAdvisoryAccess()

  const financialAdvisoryProfile = useReselector(selectFinancialAdvisoryProfile)

  const advisoryPages = useMemo(() => {
    if (isAdmin) {
      return []
    }
    if (
      financialAdvisoryProfile &&
      (financialAdvisoryProfile?.status ===
        FinancialAdvisoryProfilesStatus.active ||
        financialAdvisoryProfile?.status ===
          FinancialAdvisoryProfilesStatus.plan_completed)
    ) {
      return [
        { end: true, label: 'Overview', to: '/advisory/overview' },
        { end: true, label: 'Goals', to: '/advisory/goals' },
        { end: true, label: 'Message Advisor', to: '/advisory/messages' },
      ]
    } else if (
      financialAdvisoryProfile &&
      financialAdvisoryProfile?.status ===
        FinancialAdvisoryProfilesStatus.onboarding
    ) {
      return [
        { end: true, label: 'Onboard', to: '/advisory/overview' },
        { end: true, label: 'Message Advisor', to: '/advisory/messages' },
      ]
    } else {
      return [{ end: true, label: 'Enroll', to: '/advisory/overview' }]
    }
  }, [isAdmin, financialAdvisoryProfile])

  const insightsPages = useMemo(() => {
    const incomePlanningConfig = {
      label: 'Income Planning',
      to: '/income-planning',
      isNewFeature: true,
    }

    if (shouldDisplayPracticeInsights) {
      return [
        {
          end: true,
          label: 'Practice Insights',
          to: '/insights/overview',
        },
        {
          label: 'Monthly Reports',
          match: '/reports/bookkeeping',
          to: '/reports/bookkeeping/overview',
        },
        { label: 'Annual Reports', to: '/reports/yearly' },
        ...(shouldDisplayIncomePlanning ? [incomePlanningConfig] : []),
      ]
    }
    return [
      {
        label: 'Monthly Reports',
        match: '/reports/bookkeeping',
        to: '/reports/bookkeeping/overview',
      },
      { label: 'Annual Reports', to: '/reports/yearly' },
      {
        end: true,
        label: 'Income',
        to: '/insights/income',
      },
      {
        end: true,
        label: 'Expenses',
        to: '/insights/expenses',
      },
      {
        end: true,
        label: 'Profit',
        to: '/insights/profit',
      },
      ...(shouldDisplayIncomePlanning ? [incomePlanningConfig] : []),
    ]
  }, [shouldDisplayIncomePlanning, shouldDisplayPracticeInsights])

  if (!isAdmin && annualTaxFormsLoading) {
    return null
  }

  if (isAdmin) {
    return getConfigWithPermissions({
      overview: {
        end: true,
        label: 'Overview',
        to: '/admin/finances',
      },
      users: {
        icon: regular('users'),
        label: 'Users',
        pages: filterNulls([
          {
            end: true,
            label: 'All Users',
            to: '/admin/finances/list-users',
          },
          {
            end: true,
            label: 'All Internal Users',
            to: '/admin/finances/all-internal-users',
          },
          {
            end: true,
            label: 'All Payroll Users',
            to: '/admin/payroll/users',
          },
          {
            end: true,
            label: 'Trigger SendGrid Emails',
            to: '/admin/finances/email-users',
          },
          MASS_MESSAGE_ADMIN(user)
            ? {
                end: true,
                label: 'Zendesk Bulk Messaging',
                to: '/admin/finances/mass-messaging',
              }
            : undefined,
          QA_PAGE_ENABLED()
            ? {
                end: true,
                label: 'QA Users',
                to: '/admin/finances/qa',
              }
            : undefined,
        ]),
      },
      taxes: {
        icon: regular('university'),
        label: 'Taxes',
        pages: [
          {
            end: true,
            label: 'Annual Tax Filings',
            to: '/admin/finances/annual-tax-filings',
          },
          {
            end: true,
            label: '1099-NEC Tax Filings',
            to: '/admin/finances/abound-tax-filings',
          },
          {
            end: true,
            label: 'QTE Calculations',
            to: '/admin/finances/qte-calculations',
          },
          {
            end: true,
            label: 'Federal Income Tax Details',
            to: '/admin/finances/federal-income-tax',
          },
          {
            end: true,
            label: 'State Income Tax Details',
            to: '/admin/finances/state-income-tax',
          },
          {
            end: true,
            label: 'Tax Checklist',
            to: '/admin/finances/tax-checklist',
          },
        ],
      },
      books: {
        icon: regular('book'),
        label: 'Books',
        pages: [
          {
            end: true,
            label: 'Bookkeeping CRM',
            to: '/admin/finances/books/crm',
          },
          {
            end: true,
            label: 'Incomplete Books',
            to: '/admin/finances/books/incomplete',
          },
          { end: true, label: 'All Books', to: '/admin/finances/books' },
        ],
      },
      businessRules: {
        icon: regular('suitcase'),
        label: 'Business Rules',
        pages: [
          {
            end: true,
            label: 'Autocategorization Rules',
            to: '/admin/finances/transaction-rules',
          },
          {
            end: true,
            label: 'Chart of Accounts',
            to: '/admin/finances/transaction-categories',
          },
          {
            end: true,
            label: 'Action Items',
            to: '/admin/finances/all-action-items',
          },
          {
            end: true,
            label: 'User Document Categories',
            to: '/admin/finances/user-document-categories',
          },
          {
            end: true,
            label: 'Quarterly Tax Estimate Details',
            to: '/admin/finances/quarterly-tax-estimate-details',
          },
          {
            end: true,
            label: 'Annual Tax Details',
            to: '/admin/finances/annual-tax-details',
          },
        ],
      },
      profile: {
        icon: regular('user'),
        label: 'Profile',
        pages: [
          { end: true, label: 'Your Admin Profile', to: '/admin/profile' },
        ],
      },
    })
  }

  /**
   * For Users who have a canceled plan — we do not show anything outside of Homepage
   */
  if (isCanceledAccount) {
    return getConfigWithPermissions({
      home: {
        end: true,
        exact: true,
        icon: regular('home'),
        label: 'Home',
        to: '/accounts/canceled',
        match: '/',
      },
    })
  }

  const resourcesConfig = {
    icon: regular('question-circle'),
    label: 'Resources',
    pages: [
      {
        href: 'https://support.joinheard.com/',
        label: 'Support Center',
        newPage: true,
      },
      {
        href: 'https://www.joinheard.com/resources',
        label: 'Resource Hub',
        newPage: true,
      },
      {
        href: 'https://support.joinheard.com/hc/en-us/community/topics',
        label: 'Community',
        newPage: true,
      },
    ],
  }

  const messagesConfig = {
    end: true,
    icon: regular('envelope'),
    label: 'Messages',
    to: defaultMessagesURL,
    ornament: <NavigationCountIcon count={messageCount} />,
    disabled: !userEmailVerified,
    tooltip: !userEmailVerified
      ? 'Please verify your email to access Messages.'
      : undefined,
  }

  if (isNil(user?.onboardingItemsCompletedAt)) {
    return getConfigWithPermissions({
      onboarding: {
        end: true,
        icon: regular('compass'),
        label: 'Get Started with Heard',
        to: '/onboarding-v2',
      },
      ...(shouldEnableEarlyMessagesAccess && {
        messages: messagesConfig,
        resources: resourcesConfig,
      }),
    })
  }

  const taxesPages = isBasicPlan
    ? [
        {
          label: 'Quarterly',
          to: '/taxes/quarterly',
          end: true,
          // TODO: Remove as part of https://linear.app/heard/issue/ZEN-2064/remove-backwards-compatibility-for-priceproduct-id-checks
          icon: isBasicPlanWithoutQTE ? regular('lock') : undefined,
        },
        {
          label: 'Annual',
          to: '/taxes/annual',
          // TODO: Remove as part of https://linear.app/heard/issue/ZEN-2064/remove-backwards-compatibility-for-priceproduct-id-checks
          icon: regular('lock'),
        },
      ]
    : [
        {
          label: 'Quarterly',
          to: '/taxes/quarterly',
          end: true,
        },
        {
          label: 'Annual',
          to: '/taxes/annual',
        },
        {
          label: 'Tax Return Archive',
          to: '/taxes/archive',
        },
      ]

  return getConfigWithPermissions({
    home: {
      end: true,
      exact: true,
      icon: regular('home'),
      label: 'Home',
      to: '/dashboard',
      match: '/',
    },
    ...(!hasFreeTrialPromoCode && {
      messages: messagesConfig,
    }),
    ...(shouldDisplayPracticeInsights && {
      insights: {
        icon: regular('chart-mixed'),
        label: 'Insights',
        pages: insightsPages,
      },
    }),
    yourBusiness: {
      icon: regular('building'),
      label: 'Your Practice',
      pages: [
        {
          end: true,
          label: 'Profile',
          to: '/practice/profile',
        },
        { end: true, label: 'Documents', to: '/practice/documents' },
      ],
    },
    bookKeeping: {
      icon: regular('receipt'),
      label: 'Bookkeeping',
      pages: [
        { end: true, label: 'Transactions', to: '/transactions' },
        { label: 'Profit & Loss', to: '/reports/profit-loss/' },
        { end: true, label: 'Allocation Guides', to: '/allocation-guides' },
      ],
    },
    taxes: {
      icon: regular('university'),
      label: 'Taxes',
      pages: taxesPages,
    },
    ...(!shouldDisplayPracticeInsights && {
      insights: {
        icon: regular('chart-mixed'),
        label: 'Insights',
        pages: insightsPages,
      },
    }),
    ...payrollNavConfig,
    ...(financialAdvisoryPilotEnabled &&
      !hasFreeTrialPromoCode && {
        advisory: {
          icon: regular('envelope-open-dollar'),
          label: 'Financial Advisory',
          pages: advisoryPages,
          topRoute: '/advisory',
        },
      }),
    resources: resourcesConfig,
    ...(showReferralsLink && {
      referrals: {
        icon: regular('gift'),
        label: `Rewards - Get $${process.env.VITE_REFERRAL_FACTORY_REWARD_DOLLARS_SENDER_TOTAL}`,
        to: '/rewards',
      },
    }),
  })
}
